import throttle from 'lodash/throttle'
import { type FC, type RefObject, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { VerticalDivider } from '@/components/shared/vertical-divider/VerticalDivider'
import { VipGridLayout } from '@/components/vip/styled'
import { VipActionButtons } from '@/components/vip/vip-action-buttons/VipActionButtons'
import { VipPrice } from '@/components/vip/vip-price/VipPrice'
import {
  VipStickyHeaderConstraints,
  VipStickyHeaderContainer,
  VipStickyHeaderImageContainer,
  VipStickyHeaderTitleContainer,
} from '@/components/vip/vip-sticky-components/styled'
import { VipTitle } from '@/components/vip/vip-title/VipTitle'
import { ListingGalleryMainImage } from '@/features/listing-gallery/components/ListingGalleryMainImage'
import { type VipQueryListing } from '@/types/listings'
import { Flex } from '@/ui/atoms/flex'

export type VipStickyHeaderProps = {
  /**
   * Reference of the element the sticky header needs to pass to disappear
   */
  bottomVisibilityLimitRef: RefObject<HTMLDivElement>

  listing: VipQueryListing
  /**
   * Reference of the element the sticky header needs to pass by to be visible
   */
  topVisibilityRef: RefObject<HTMLDivElement>

  isSellerVip: boolean
}

export const VipStickyHeader: FC<VipStickyHeaderProps> = ({
  bottomVisibilityLimitRef,
  isSellerVip,
  listing,
  topVisibilityRef,
}) => {
  const { spacing } = useTheme()
  const { categoryId, flags, imageUrls, price, title, type } = listing
  const [isVisible, setIsVisible] = useState<boolean>(false)

  // Effect to handle scroll event
  useEffect(() => {
    const handleScroll = throttle(() => {
      if (topVisibilityRef.current && bottomVisibilityLimitRef.current) {
        const { top: displayTop } = topVisibilityRef.current.getBoundingClientRect()
        const { top: hideTop } = bottomVisibilityLimitRef.current.getBoundingClientRect()

        if (displayTop < 0 && hideTop > 0) {
          setIsVisible(true)
        } else {
          setIsVisible(false)
        }
      }
    }, 100)

    window.addEventListener('scroll', handleScroll)

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll)
      // Any pending throttled executions should be canceled when the component unmounts to prevent memory leaks
      handleScroll.cancel()
    }
  }, [topVisibilityRef, bottomVisibilityLimitRef])

  return (
    <VipStickyHeaderConstraints isVisible={isVisible}>
      <VipGridLayout>
        <VipStickyHeaderContainer data-testid="vip-sticky-header">
          <Flex gap={spacing.default} alignItems="center">
            {imageUrls.length ? (
              <VipStickyHeaderImageContainer>
                <ListingGalleryMainImage imageUrls={imageUrls} notInteractive />
              </VipStickyHeaderImageContainer>
            ) : null}

            <Flex gap={spacing.defaultSmall}>
              <VipPrice
                categoryId={categoryId}
                listingFlags={flags}
                price={price}
                isSimplified
                size="medium"
              />

              <VerticalDivider />

              <VipStickyHeaderTitleContainer>
                <VipTitle adType={type} as="h2" size="medium" title={title} />
              </VipStickyHeaderTitleContainer>
            </Flex>
          </Flex>

          <Flex>
            <VipActionButtons listing={listing} isSimplified isSellerVip={isSellerVip} />
          </Flex>
        </VipStickyHeaderContainer>
      </VipGridLayout>
    </VipStickyHeaderConstraints>
  )
}
